import React, { Component } from "react";
import {
  toFixed,
  GET,
  loading,
  Select,
  Navbar,
  XLSX,
  saveAs,
} from "../../components/CustomComponent.js";

const COLUMNS = [
  "Month",
  "Payin Orders", "Payin Amount", "Payin Fee", "Payin Cost", "Payin Commission", "Payin Profit",
  "Payout Orders", "Payout Amount", "Payout Fee", "Payout Cost", "Payout Commission", "Payout Profit",
];

export default class AnnualProfits extends Component {
  constructor(props) {
    super(props);
    this.state = {
      year: new Date().getFullYear(),
      filter_currency: "THB",
      months: [],
      total: null,
      loaded: false,
    };
  }

  async componentDidMount() {
    loading(false);
    await this.Get();
  }

  Get = async () => {
    const { year, filter_currency } = this.state;
    loading(true);
    const result = await GET("v1/admin/profits/annual-report", {
      year,
      currency: filter_currency,
    });
    if (result?.status) {
      this.setState({
        months: result.data?.months || [],
        total: result.data?.total || null,
        loaded: true,
      });
    } else {
      this.setState({ months: [], total: null, loaded: true });
    }
    loading(false);
  };

  // Build the rows for one currency payload (used by both display and export).
  BuildRows = (payload) => {
    const rows = (payload?.months || []).map((m) => [
      m.month_label,
      m.payin.orders,
      m.payin.amount,
      m.payin.fee,
      m.payin.cost,
      m.payin.commission,
      m.payin.profit,
      m.payout.orders,
      m.payout.amount,
      m.payout.fee,
      m.payout.cost,
      m.payout.commission,
      m.payout.profit,
    ]);
    const t = payload?.total;
    if (t) {
      rows.push([
        "TOTAL",
        t.payin.orders, t.payin.amount, t.payin.fee, t.payin.cost, t.payin.commission, t.payin.profit,
        t.payout.orders, t.payout.amount, t.payout.fee, t.payout.cost, t.payout.commission, t.payout.profit,
      ]);
    }
    return rows;
  };

  ExportExcel = async () => {
    const { year } = this.state;
    loading(true);
    // Fetch both currencies so the workbook mirrors the source report (THB + LAK sheets).
    const [thb, lak] = await Promise.all([
      GET("v1/admin/profits/annual-report", { year, currency: "THB" }),
      GET("v1/admin/profits/annual-report", { year, currency: "LAK" }),
    ]);
    const workbook = XLSX.utils.book_new();
    [
      { code: "THB", res: thb },
      { code: "LAK", res: lak },
    ].forEach(({ code, res }) => {
      const rows = this.BuildRows(res?.data);
      const aoa = [COLUMNS, ...rows];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      XLSX.utils.book_append_sheet(workbook, ws, code);
    });
    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buffer], { type: "application/octet-stream" }), `profit-report-${year}.xlsx`);
    loading(false);
  };

  ExportCSV = () => {
    const { year, filter_currency, months, total } = this.state;
    const rows = this.BuildRows({ months, total });
    const escape = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [COLUMNS, ...rows].map((r) => r.map(escape).join(",")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `profit-report-${year}-${filter_currency}.csv`);
  };

  render() {
    const { year, filter_currency, months, total, loaded } = this.state;
    const yearOptions = [];
    const nowYear = new Date().getFullYear();
    for (let y = nowYear; y >= nowYear - 6; y--) {
      yearOptions.push({ value: y, label: String(y) });
    }

    const cell = (v) => toFixed(v, 2);
    const num = (v) => Number(v) || 0;
    const cnt = (v) => num(v).toLocaleString();
    // สีตัวเลขกำไร: ติดลบ = แดง, ศูนย์ = จาง
    const profitClass = (v) =>
      num(v) < 0 ? "ar-neg" : num(v) === 0 ? "ar-zero" : "";
    const currency = global.currency?.find(
      (c) => c.currency_code === filter_currency
    );
    const currencySymbol =
      filter_currency === "THB" ? "฿" : filter_currency === "LAK" ? "₭" : "";

    const kpis = total
      ? [
          {
            label: "Deposit Profit",
            value: cell(total.payin.profit),
            sub: `${cnt(total.payin.orders)} orders · Amount ${cell(total.payin.amount)}`,
            accent: "#14b8a6",
            icon: "",
            negative: num(total.payin.profit) < 0,
          },
          {
            label: "Withdrawal Profit",
            value: cell(total.payout.profit),
            sub: `${cnt(total.payout.orders)} orders · Amount ${cell(total.payout.amount)}`,
            accent: "#f59e0b",
            icon: "",
            negative: num(total.payout.profit) < 0,
          },
          {
            label: "Total Orders",
            value: (num(total.payin.orders) + num(total.payout.orders)).toLocaleString(),
            sub: `Deposit ${num(total.payin.orders).toLocaleString()} · Withdrawal ${num(total.payout.orders).toLocaleString()}`,
            accent: "#6366f1",
            icon: "",
            plain: true,
          },
          {
            label: "Net Profit",
            value: cell(num(total.payin.profit) + num(total.payout.profit)),
            sub: "Deposit profit + Withdrawal profit",
            accent: "#d4af37",
            icon: "",
            negative: num(total.payin.profit) + num(total.payout.profit) < 0,
            highlight: true,
          },
        ]
      : [];

    const renderRow = (m, isTotal) => (
      <tr key={isTotal ? "total" : m.month} className={isTotal ? "ar-total" : ""}>
        <td data-label="Month" className="ar-sticky">
          {isTotal ? (
            <div className="ar-month-badge">
              <span className="ar-month-badge-total">Σ</span>
              TOTAL {year}
            </div>
          ) : (
            <div className="ar-month-badge">
              <span>{String(m.month).padStart(2, "0")}</span>
              {m.month_label}
            </div>
          )}
        </td>
        <td data-label="Payin Orders" className="text-center ar-group-start">{cnt(m.payin.orders)}</td>
        <td data-label="Payin Amount" className="text-end">{cell(m.payin.amount)}</td>
        <td data-label="Payin Fee" className="text-end">{cell(m.payin.fee)}</td>
        <td data-label="Payin Cost" className="text-end ar-muted">{cell(m.payin.cost)}</td>
        <td data-label="Payin Commission" className="text-end ar-muted">{cell(m.payin.commission)}</td>
        <td data-label="Payin Profit" className={`text-end ar-dep-profit ${profitClass(m.payin.profit)}`}>{cell(m.payin.profit)}</td>
        <td data-label="Payout Orders" className="text-center ar-group-start">{cnt(m.payout.orders)}</td>
        <td data-label="Payout Amount" className="text-end">{cell(m.payout.amount)}</td>
        <td data-label="Payout Fee" className="text-end">{cell(m.payout.fee)}</td>
        <td data-label="Payout Cost" className="text-end ar-muted">{cell(m.payout.cost)}</td>
        <td data-label="Payout Commission" className="text-end ar-muted">{cell(m.payout.commission)}</td>
        <td data-label="Payout Profit" className={`text-end ar-wd-profit ${profitClass(m.payout.profit)}`}>{cell(m.payout.profit)}</td>
      </tr>
    );

    return (
      <div className="w-100 min-vh-100 premium-report">
        <Navbar />
        <div className="body p-3 mt-3">
          {/* ===== Hero ===== */}
          <div className="ar-hero mb-4">
            <div className="ar-hero-head">
              <div>
                <div className="ar-eyebrow mb-1">Annual Report</div>
                <div className="ar-title">Annual Profit Report</div>
                <div className="ar-subtitle">
                  Monthly payin / payout profit summary by year and currency
                </div>
              </div>
              <div className="ar-hero-actions">
                <button
                  className="annual-profits.read ar-btn-ghost"
                  onClick={this.ExportCSV}
                >
                  <span className="icon me-2">{""}</span>Export CSV
                </button>
                <button
                  className="annual-profits.read ar-btn-ghost"
                  onClick={this.ExportExcel}
                >
                  <span className="icon me-2">{""}</span>Export Excel
                </button>
                <div className="text-end">
                  <div className="ar-year">{year}</div>
                  <div className="ar-year-caption">
                    {currency?.image}
                    <span className="ms-1">{filter_currency}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="ar-toolbar ar-filter mt-3">
<div className="ar-filter-head">
<span className="ar-toolbar-label"><span className="icon me-2">{""}</span>Filter</span>
</div>
<div className="ar-filter-row">
<div className="ar-filter-field">
<span className="ar-filter-label">Year</span>
<Select
                    className="form-control"
                    placeholder="Year..."
                    options={yearOptions}
                    value={year}
                    default={false}
                    onChange={(v) => {
                      this.setState({ year: Number(v) }, () => this.Get());
                    }}
                  />
</div>
</div>
<div className="ar-filter-foot">
<div className="d-flex align-items-center gap-2">
                  {global.currency?.map((item) => (
                    <button
                      key={item.currency_code}
                      className={`ar-pill ${
                        item.currency_code === filter_currency ? "active" : ""
                      }`}
                      onClick={() => {
                        this.setState({ filter_currency: item.currency_code }, () => this.Get());
                      }}
                    >
                      {item.image}
                      <span className="ms-1">{item.currency_code}</span>
                    </button>
                  ))}
                </div>
</div>
</div>
          </div>

          {/* ===== KPI summary (จากแถว TOTAL) ===== */}
          {kpis.length > 0 && (
            <div className="row g-3 mb-4">
              {kpis.map((k) => (
                <div className="col-12 col-sm-6 col-xl-3" key={k.label}>
                  <div
                    className={`ar-kpi ${k.highlight ? "ar-kpi-highlight" : ""}`}
                    style={{ "--ar-accent": k.accent }}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="ar-kpi-label">{k.label}</div>
                      <div className="ar-kpi-icon">
                        <span className="icon">{k.icon}</span>
                      </div>
                    </div>
                    <div className={`ar-kpi-value ${k.negative ? "ar-neg" : ""}`}>
                      {!k.plain && (
                        <span className="ar-kpi-currency">{currencySymbol}</span>
                      )}
                      {k.value}
                    </div>
                    <div className="ar-kpi-sub">{k.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ===== Table ===== */}
          <div className="ar-table-card">
            <div className="ar-table-head">
              <div>
                <div className="ar-table-title">Monthly Breakdown</div>
                <div className="ar-table-caption">
                  {year} · {filter_currency} · {months.length} month{months.length === 1 ? "" : "s"}
                </div>
              </div>
              <div className="d-none d-md-flex align-items-center gap-3 ar-legend">
                <span><i className="ar-legend-dot ar-legend-dep"></i>Deposit</span>
                <span><i className="ar-legend-dot ar-legend-wd"></i>Withdrawal</span>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-hover text-nowrap ar-table">
                <thead>
                  <tr>
                    <td rowSpan={2} className="align-middle ar-sticky">Month</td>
                    <td colSpan={6} className="text-center ar-group ar-group-dep ar-group-start">
                      <span className="icon me-2">{""}</span>Deposit
                    </td>
                    <td colSpan={6} className="text-center ar-group ar-group-wd ar-group-start">
                      <span className="icon me-2">{""}</span>Withdrawal
                    </td>
                  </tr>
                  <tr>
                    <td className="text-center ar-group-start">Orders</td>
                    <td className="text-end">Amount</td>
                    <td className="text-end">Fee</td>
                    <td className="text-end">Cost</td>
                    <td className="text-end">Commission</td>
                    <td className="text-end ar-head-profit">Profit</td>
                    <td className="text-center ar-group-start">Orders</td>
                    <td className="text-end">Amount</td>
                    <td className="text-end">Fee</td>
                    <td className="text-end">Cost</td>
                    <td className="text-end">Commission</td>
                    <td className="text-end ar-head-profit">Profit</td>
                  </tr>
                </thead>
                <tbody>
                  {months.map((m) => renderRow(m, false))}
                  {total && renderRow(total, true)}
                  {loaded && months.length === 0 && (
                    <tr>
                      <td colSpan={13} className="text-center py-5">
                        <div className="ar-empty">
                          <span className="icon d-block mb-2">{""}</span>
                          No data for {year} · {filter_currency}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
